<template>
  <div class="universal-feed">
    <!-- Optional title -->
    <h2 v-if="title" class="feed-title">{{ title }}</h2>
    
    <!-- Post creation UI (when enabled) -->
    <div v-if="showPostCreation" class="post-creation-section">
      <PostCreationForm 
        :neighborhood="currentNeighborhood"
        @post-created="handlePostCreated"
        @show-auth="$emit('show-auth')"
      />
    </div>

    <!-- Loading state -->
    <div v-if="loading && posts.length === 0" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading posts...</p>
    </div>

    <!-- Empty/Error state (combined for cleaner UX) -->
    <div v-if="!loading && posts.length === 0" class="empty-state">
      <p>No posts available.</p>
      <p v-if="showPostCreation">Be the first to share something!</p>
    </div>

    <!-- Top ad - appears before first post or when feed is empty -->
    <div class="feed-ad-banner top-ad">
      <div class="ad-banner-container">
        <h5 class="ad-tip">Advertisement</h5>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-format="fluid"
             data-ad-layout-key="-fb+5w+4e-db+86"
             data-ad-client="ca-pub-1179423790307792"
             data-ad-slot="7985728113"></ins>
      </div>
    </div>

    <!-- Posts list -->
    <div v-if="posts.length > 0" class="posts-container">
      <template v-for="(post, index) in posts" :key="post.id">
        <PostCard
          :post="post"
          :with-border="true"
          :show-actions="true"
          :show-comments="true"
          :time-format="timeFormat"
          @vote-up="handleVoteUp"
          @vote-down="handleVoteDown"
          @share="handleShare"
          @comment="handleComment"
          @show-auth="$emit('show-auth')"
          @post-deleted="handlePostDeleted"
        />

        <!-- Insert banner ad every 5th post -->
        <div v-if="(index + 1) % 5 === 0 && index < posts.length - 1" class="feed-ad-banner">
          <div class="ad-banner-container">
            <h5 class="ad-tip">Advertisement</h5>
            <ins class="adsbygoogle"
                 style="display:block"
                 data-ad-format="fluid"
                 data-ad-layout-key="-fb+5w+4e-db+86"
                 data-ad-client="ca-pub-1179423790307792"
                 data-ad-slot="7985728113"></ins>
          </div>
        </div>
      </template>
    </div>

    <!-- Pagination controls (for pagination mode) -->
    <div v-if="paginationType === 'pagination' && posts.length > 0" class="pagination-controls">
      <button 
        @click="loadPreviousPage" 
        :disabled="currentPage <= 1 || loading"
        class="pagination-btn"
      >
        Previous
      </button>
      
      <span class="page-info">
        Page {{ currentPage }} {{ totalPages ? `of ${totalPages}` : '' }}
      </span>
      
      <button 
        @click="loadNextPage" 
        :disabled="!hasNextPage || loading"
        class="pagination-btn"
      >
        Next
      </button>
    </div>

    <!-- Infinite scroll loading indicator -->
    <div v-if="paginationType === 'infinite' && loading && posts.length > 0" class="infinite-loading">
      <div class="loading-spinner small"></div>
      <p>Loading more posts...</p>
    </div>

    <!-- End of feed indicator -->
    <div v-if="paginationType === 'infinite' && !hasNextPage && posts.length > 0" class="end-of-feed">
      <p>You've reached the end!</p>
    </div>
  </div>
</template>

<script>
import PostCard from './PostCard.vue';
import PostCreationForm from './PostCreationForm.vue';
import { usePostManagement } from '../../composables/usePostManagement.js';
import { useAmplifyClient } from '../../composables/useAmplifyClient.js';

export default {
  name: 'UniversalFeed',
  components: {
    PostCard,
    PostCreationForm
  },
  props: {
    filter: {
      type: Object,
      default: null
    },
    sortBy: {
      type: String,
      default: 'createdAt'
    },
    sortDirection: {
      type: String,
      default: 'DESC',
      validator: (value) => ['ASC', 'DESC'].includes(value)
    },
    limit: {
      type: Number,
      default: 15
    },
    paginationType: {
      type: String,
      default: 'infinite',
      validator: (value) => ['pagination', 'infinite'].includes(value)
    },
    showPostCreation: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    timeFormat: {
      type: String,
      default: 'relative',
      validator: (value) => ['relative', 'date'].includes(value)
    }
  },
  emits: ['show-auth', 'post-created', 'vote-up', 'vote-down', 'share', 'comment'],
  computed: {
    currentNeighborhood() {
      // Extract neighborhood from filter prop
      return this.filter?.neighborhood?.eq || null;
    }
  },
  data() {
    return {
      posts: [],
      loading: false,
      error: null,
      currentPage: 1,
      totalPages: null,
      hasNextPage: false,
      nextToken: null,
      previousTokens: [], // Stack for pagination backwards
      updateSubscription: null
    };
  },
  mounted() {
    // Only load posts if we have a filter or if filter is explicitly null (which means load all)
    // Props are available synchronously in mounted() hook
    if (this.filter !== undefined) {
      this.loadPosts();
    }

    // Setup infinite scroll if enabled
    if (this.paginationType === 'infinite') {
      this.setupInfiniteScroll();
    }

    // Setup real-time subscriptions for post updates
    this.setupSubscriptions();

    // Initialize ads
    this.initializeAds();
  },
  beforeUnmount() {
    if (this.paginationType === 'infinite') {
      this.cleanupInfiniteScroll();
    }

    // Cleanup subscriptions
    this.cleanupSubscriptions();
  },
  watch: {
    // Watch for filter changes and reload posts
    filter: {
      handler(newFilter, oldFilter) {
        // Only reload if filter actually changed
        if (JSON.stringify(newFilter) !== JSON.stringify(oldFilter)) {
          this.loadPosts(true);
        }
      },
      deep: true,
      immediate: false // Don't run on initial mount since mounted() handles that
    }
  },
  methods: {
    async loadPosts(reset = true) {
      try {
        this.loading = true;
        this.error = null;

        if (reset) {
          this.posts = [];
          this.currentPage = 1;
          this.nextToken = null;
          this.previousTokens = [];
        }

        const postManager = usePostManagement();

        // Build query parameters
        const queryParams = {
          limit: this.limit,
          filter: this.filter
        };

        // Add pagination token if continuing
        if (!reset && this.nextToken) {
          queryParams.nextToken = this.nextToken;
        }

        // Add sorting (GraphQL uses different syntax)
        if (this.sortBy && this.sortDirection) {
          queryParams.sortDirection = this.sortDirection;
          // Note: GraphQL sorting implementation may vary by schema
        }

        const result = await postManager.loadPosts(queryParams);
        
        
        if (reset) {
          this.posts = result.items || [];
        } else {
          this.posts.push(...(result.items || []));
        }

        this.hasNextPage = !!result.nextToken;
        this.nextToken = result.nextToken;

        // For pagination, calculate total pages if available
        if (this.paginationType === 'pagination' && result.total) {
          this.totalPages = Math.ceil(result.total / this.limit);
        }

        // Initialize ads after posts are loaded
        // Calling directly (without $nextTick) since initializeAds() already uses requestIdleCallback
        this.initializeAds();

      } catch (error) {
        console.error('Error loading posts:', error);
        this.error = error.message || 'Failed to load posts';
      } finally {
        this.loading = false;
      }
    },

    async loadNextPage() {
      if (!this.hasNextPage || this.loading) return;

      if (this.paginationType === 'pagination') {
        // Store current token for going back
        this.previousTokens.push(this.nextToken);
        this.currentPage++;
      }

      await this.loadPosts(false);
    },

    async loadPreviousPage() {
      if (this.currentPage <= 1 || this.loading) return;
      
      // For pagination, we need to reload from a previous token
      this.currentPage--;
      const previousToken = this.previousTokens.pop();
      
      // Reset and load with previous token
      this.nextToken = previousToken;
      await this.loadPosts();
    },

    setupInfiniteScroll() {
      this.infiniteScrollHandler = () => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.offsetHeight;
        const threshold = documentHeight - 1000; // Load 1000px before bottom

        if (scrollPosition >= threshold && this.hasNextPage && !this.loading) {
          this.loadNextPage();
        }
      };

      window.addEventListener('scroll', this.infiniteScrollHandler);
    },

    cleanupInfiniteScroll() {
      if (this.infiniteScrollHandler) {
        window.removeEventListener('scroll', this.infiniteScrollHandler);
      }
    },

    async handlePostCreated(post) {
      // Add new post to the beginning of the feed
      this.posts.unshift(post);
      this.$emit('post-created', post);
    },

    handleVoteUp(post) {
      this.$emit('vote-up', post);
    },

    handleVoteDown(post) {
      this.$emit('vote-down', post);
    },

    handleShare(post) {
      this.$emit('share', post);
    },

    handleComment(post) {
      this.$emit('comment', post);
    },

    handlePostDeleted(postId) {
      // Remove the deleted post from the local posts array
      const index = this.posts.findIndex(post => post.id === postId);
      if (index !== -1) {
        this.posts.splice(index, 1);
      }
    },

    // Setup real-time subscriptions for post updates
    setupSubscriptions() {
      const { client } = useAmplifyClient();
      
      // Subscribe to Post updates (votes, content changes)
      // Only fetch essential fields to avoid null reference errors
      const updateSubscription = `
        subscription OnUpdatePost {
          onUpdatePost {
            id
            upvoteCount
            downvoteCount
            updatedAt
          }
        }
      `;

      this.updateSubscription = client.graphql({
        query: updateSubscription
      }).subscribe({
        next: (result) => {
          // Check for GraphQL errors
          if (result.errors && result.errors.length > 0) {
            // Continue processing if we have data despite errors
          }
          
          const updatedPost = result.data?.onUpdatePost;
          if (!updatedPost || !updatedPost.id) {
            return;
          }
          
          
          // Find and update the post in our local array
          const postIndex = this.posts.findIndex(post => post.id === updatedPost.id);
          if (postIndex !== -1) {
            // Only update the specific fields that changed (vote counts, updatedAt)
            // Preserve all other existing data
            const existingPost = this.posts[postIndex];
            this.posts[postIndex] = {
              ...existingPost,
              upvoteCount: updatedPost.upvoteCount,
              downvoteCount: updatedPost.downvoteCount,
              updatedAt: updatedPost.updatedAt
            };
          }
        },
        error: (error) => {
        }
      });
    },

    // Cleanup subscriptions
    cleanupSubscriptions() {
      if (this.updateSubscription) {
        this.updateSubscription.unsubscribe();
        this.updateSubscription = null;
      }
    },

    // Public method for parent components to refresh
    async refresh() {
      await this.loadPosts(true);
    },

    initializeAds() {
      // Initialize Google AdSense ads after browser is idle (non-blocking)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          try {
            if (typeof window !== 'undefined' && window.adsbygoogle) {
              // Push all new ads to AdSense queue
              const ads = document.querySelectorAll('.adsbygoogle:not([data-adsbygoogle-status])');
              ads.forEach(() => {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
              });
            }
          } catch (error) {
            console.warn('AdSense initialization failed:', error);
          }
        }, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          try {
            if (typeof window !== 'undefined' && window.adsbygoogle) {
              // Push all new ads to AdSense queue
              const ads = document.querySelectorAll('.adsbygoogle:not([data-adsbygoogle-status])');
              ads.forEach(() => {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
              });
            }
          } catch (error) {
            console.warn('AdSense initialization failed:', error);
          }
        }, 1000);
      }
    }
  }
};
</script>

<style scoped>
.universal-feed {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.feed-title {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
}

.post-creation-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-spinner.small {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc3545;
  margin-bottom: 20px;
}

.retry-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.retry-btn:hover {
  background: #0056b3;
}

.posts-container {
  margin-bottom: 30px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
}

.pagination-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.pagination-btn:hover:not(:disabled) {
  background: #0056b3;
}

.pagination-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: #333;
}

.infinite-loading {
  text-align: center;
  padding: 20px;
  color: #666;
}

.end-of-feed {
  text-align: center;
  padding: 20px;
  color: #999;
  font-style: italic;
}

/* Advertisement styles */
.feed-ad-banner {
  margin: 30px 0;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.feed-ad-banner.top-ad {
  margin: 0 0 30px 0;
  border-top: none;
}

.ad-banner-container {
  text-align: center;
  padding: 15px 10px 10px 10px;
  background: transparent;
  margin: 0;
}

.ad-tip {
  font-size: 12px;
  color: #666;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.adsbygoogle {
  margin: 0 auto;
}

/* Responsive ad container */
@media (max-width: 768px) {
  .feed-ad-banner {
    margin: 20px 0;
  }

  .ad-banner-container {
    padding: 15px;
  }
}
</style>